import type { BerThongSheet } from '../../services/berThongService'

export function sheetBetLabel(sheet: Pick<BerThongSheet, 'betType'>) {
  return sheet.betType === 'top2' ? '2 ตัวบน' : '2 ตัวล่าง'
}

export function formatCloseTime(value?: string | null) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

/** Compact close label — same style as lobby-huay listall cards. */
export function formatCloseTimeCompact(value?: string | null) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Compact countdown (legacy / tight spaces). */
export function formatCountdown(closeAt?: string | null, nowMs = Date.now()) {
  const parts = getCountdownParts(closeAt, nowMs)
  if (!parts) return ''
  if (parts.closed) return 'ปิดรับแทงแล้ว'
  if (parts.days > 0) {
    return `เหลือ ${parts.days} วัน ${String(parts.hours).padStart(2, '0')}:${String(parts.minutes).padStart(2, '0')}:${String(parts.seconds).padStart(2, '0')}`
  }
  if (parts.hours > 0) {
    return `เหลือ ${parts.hours}:${String(parts.minutes).padStart(2, '0')}:${String(parts.seconds).padStart(2, '0')}`
  }
  return `เหลือ ${parts.minutes}:${String(parts.seconds).padStart(2, '0')}`
}

export type CountdownParts = {
  closed: boolean
  days: number
  hours: number
  minutes: number
  seconds: number
  totalMs: number
}

export function getCountdownParts(closeAt?: string | null, nowMs = Date.now()): CountdownParts | null {
  if (!closeAt) return null
  const end = new Date(closeAt).getTime()
  if (Number.isNaN(end)) return null
  // Match purchase gate: open while closeAt >= now (server rejects only when closeAt < now)
  const totalMs = end - nowMs
  if (totalMs < 0) {
    return { closed: true, days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0 }
  }
  const totalSec = Math.floor(totalMs / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60
  return { closed: false, days, hours, minutes, seconds, totalMs }
}

/** Full countdown including seconds so the UI visibly ticks. */
export function formatCountdownDetailed(closeAt?: string | null, nowMs = Date.now()) {
  const parts = getCountdownParts(closeAt, nowMs)
  if (!parts) return ''
  if (parts.closed) return 'ปิดรับแทงแล้ว'
  const chunks: string[] = []
  if (parts.days > 0) chunks.push(`${parts.days} วัน`)
  chunks.push(`${parts.hours} ชั่วโมง`)
  chunks.push(`${parts.minutes} นาที`)
  chunks.push(`${parts.seconds} วินาที`)
  return `เหลือ ${chunks.join(' ')}`
}

export function isClosingSoon(closeAt?: string | null, nowMs = Date.now()) {
  if (!closeAt) return false
  const end = new Date(closeAt).getTime()
  if (Number.isNaN(end)) return false
  const mins = (end - nowMs) / 60000
  return mins > 0 && mins <= 30
}

export function isBettingClosed(
  sheet: Pick<BerThongSheet, 'status' | 'closeAt'> | null | undefined,
  nowMs = Date.now(),
) {
  if (!sheet) return true
  if (sheet.status !== 'open') return true
  if (!sheet.closeAt) return false
  const end = new Date(sheet.closeAt).getTime()
  if (Number.isNaN(end)) return false
  return nowMs > end
}

export function isSheetBuyable(sheet: BerThongSheet, nowMs = Date.now()) {
  return sheet.status === 'open' && !isBettingClosed(sheet, nowMs)
}

export type BerThongHuayGroup = {
  key: string
  huayId: string
  huayName: string
  openSheets: BerThongSheet[]
  closedSheets: BerThongSheet[]
  soonestCloseAt: string | null
}

export function huayKeyOf(sheet: Pick<BerThongSheet, 'huayId' | 'huayName'>) {
  const id = String(sheet.huayId || '').trim()
  if (id) return `id:${id}`
  const name = String(sheet.huayName || 'อื่นๆ').trim() || 'อื่นๆ'
  return `name:${encodeURIComponent(name)}`
}

export function parseHuayKey(huayKey: string): { huayId: string; huayName: string } {
  if (huayKey.startsWith('id:')) {
    return { huayId: huayKey.slice(3), huayName: '' }
  }
  if (huayKey.startsWith('name:')) {
    try {
      return { huayId: '', huayName: decodeURIComponent(huayKey.slice(5)) }
    } catch {
      return { huayId: '', huayName: huayKey.slice(5) }
    }
  }
  return { huayId: huayKey, huayName: '' }
}

export function matchesHuayKey(sheet: BerThongSheet, huayKey: string) {
  return huayKeyOf(sheet) === huayKey
}

export function groupSheetsByHuay(sheets: BerThongSheet[], nowMs = Date.now()): BerThongHuayGroup[] {
  const map = new Map<string, BerThongHuayGroup>()
  for (const sheet of sheets) {
    const key = huayKeyOf(sheet)
    let group = map.get(key)
    if (!group) {
      group = {
        key,
        huayId: String(sheet.huayId || ''),
        huayName: String(sheet.huayName || 'อื่นๆ').trim() || 'อื่นๆ',
        openSheets: [],
        closedSheets: [],
        soonestCloseAt: null,
      }
      map.set(key, group)
    }
    if (isSheetBuyable(sheet, nowMs)) {
      group.openSheets.push(sheet)
      if (sheet.closeAt) {
        if (!group.soonestCloseAt || new Date(sheet.closeAt) < new Date(group.soonestCloseAt)) {
          group.soonestCloseAt = sheet.closeAt
        }
      }
    } else {
      group.closedSheets.push(sheet)
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const aOpen = a.openSheets.length
    const bOpen = b.openSheets.length
    if (aOpen !== bOpen) return bOpen - aOpen
    return a.huayName.localeCompare(b.huayName, 'th')
  })
}
